import React, { useState, useEffect } from "react";
import theme from "../theme";

export default function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIosPrompt, setIsIosPrompt] = useState(false);

  useEffect(() => {
    // 1. Detect if the user is on an iOS device (Safari doesn't support beforeinstallprompt)
    const isIos = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent);
    };

    // 2. Detect if the app is already installed (standalone mode)
    const isInStandaloneMode = () =>
      ('standalone' in window.navigator) && (window.navigator.standalone);

    // If on iOS and not already installed, show custom instruction prompt
    if (isIos() && !isInStandaloneMode()) {
      setIsIosPrompt(true);
      setIsVisible(true);
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      // Update UI to notify the user they can add to home screen
      setIsVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    setIsVisible(false);
    if (!deferredPrompt) {
      return;
    }
    // Show the install prompt
    deferredPrompt.prompt();
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);
    // We've used the prompt, and can't use it again, discard it
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div style={{
      position: "fixed",
      bottom: 80, // Above bottom nav
      left: 16,
      right: 16,
      background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
      color: "white",
      borderRadius: theme.radius.lg,
      padding: "16px",
      boxShadow: "0 8px 24px rgba(15,23,42,0.25)",
      border: "1px solid rgba(255,255,255,0.1)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      zIndex: 9999,
      animation: "slideUp 0.3s ease-out forwards",
    }}>
      <style>
        {`
          @keyframes slideUp {
            from { transform: translateY(100px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>
      
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
        <img src="/logo192.png" alt="App Logo" style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0 }} />
        <div>
          <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 800, color: "white" }}>
            Install UrbaMart
          </p>
          <p style={{ margin: 0, fontSize: 12, color: "rgba(255,255,255,0.8)", lineHeight: 1.3 }}>
            {isIosPrompt 
              ? "To install, tap the Share icon in your browser menu and select 'Add to Home Screen'."
              : "Add to your home screen for a faster, app-like experience!"}
          </p>
        </div>
      </div>
      
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
        {!isIosPrompt && (
          <button
            onClick={handleInstallClick}
            style={{
              background: "#F59E0B", color: "white", border: "none",
              padding: "8px 16px", borderRadius: theme.radius.md, fontWeight: 800,
              fontSize: 13, cursor: "pointer", boxShadow: "0 2px 6px rgba(245,158,11,0.3)",
            }}
          >
            Install App
          </button>
        )}
        <button
          onClick={handleDismiss}
          style={{
            background: isIosPrompt ? "rgba(255,255,255,0.15)" : "transparent", 
            color: isIosPrompt ? "white" : "rgba(255,255,255,0.5)", 
            border: "none",
            padding: isIosPrompt ? "8px 16px" : "4px 8px", 
            fontSize: isIosPrompt ? 13 : 12, 
            cursor: "pointer", 
            fontWeight: isIosPrompt ? 800 : 600,
            borderRadius: theme.radius.md,
          }}
        >
          {isIosPrompt ? "Got it" : "Not now"}
        </button>
      </div>
    </div>
  );
}
